import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DragToBucketScene } from '@/types/gameSpec';
import { shuffle, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

/**
 * Click an item, then click a bucket to assign it.
 * Auto-completes when all items are placed. (Drag is added in a later wave.)
 */
export const DragToBucketSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<DragToBucketScene>) => {
  const order = useMemo(
    () => shuffle(scene.items.map((_, i) => i)),
    [scene.id],
  );
  const [selected, setSelected] = useState<number | null>(null);
  const [placement, setPlacement] = useState<Record<number, number>>({});
  const [revealed, setRevealed] = useState(false);

  const placeIn = (bucketIdx: number) => {
    if (revealed || selected === null) return;
    const next = { ...placement, [selected]: bucketIdx };
    setSelected(null);
    setPlacement(next);
    if (Object.keys(next).length === scene.items.length) {
      setRevealed(true);
      const ok = scene.items.every(
        (it, i) => next[i] === it.bucket_index,
      );
      setTimeout(() => onAnswer(ok, { placement: next }), 900);
    }
  };

  const placedItems = (bucketIdx: number) =>
    Object.entries(placement)
      .filter(([, b]) => b === bucketIdx)
      .map(([i]) => Number(i));

  const unplaced = order.filter((i) => placement[i] === undefined);

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.drag_to_bucket}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        {unplaced.length > 0 && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Pick an item, then choose a bucket:
            </p>
            <div className="flex flex-wrap gap-2">
              {unplaced.map((i) => (
                <Button
                  key={i}
                  variant={selected === i ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelected(i)}
                  disabled={revealed}
                >
                  {scene.items[i].label}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: `repeat(${Math.min(scene.buckets.length, 3)}, minmax(0,1fr))`,
          }}
        >
          {scene.buckets.map((b, bIdx) => (
            <button
              key={bIdx}
              type="button"
              onClick={() => placeIn(bIdx)}
              disabled={revealed || selected === null}
              className={cn(
                'rounded-lg border-2 border-dashed p-3 min-h-[120px] text-left transition',
                selected !== null && !revealed && 'border-primary bg-primary/5 hover:bg-primary/10',
                'disabled:cursor-not-allowed',
              )}
            >
              <div className="text-sm font-semibold mb-2">{b}</div>
              <div className="flex flex-wrap gap-1">
                {placedItems(bIdx).map((i) => {
                  const correct = revealed && scene.items[i].bucket_index === bIdx;
                  const wrong = revealed && scene.items[i].bucket_index !== bIdx;
                  return (
                    <span
                      key={i}
                      className={cn(
                        'text-xs rounded px-2 py-1 bg-muted border',
                        correct && 'border-green-500 bg-green-50 text-green-900',
                        wrong && 'border-destructive bg-red-50 text-red-900',
                      )}
                    >
                      {scene.items[i].label}
                    </span>
                  );
                })}
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
